// #3405 (workstream placed-by-division) -- shared helpers for the Score Admin
// "Placed by division" setting: which classes a change would re-place, which
// of those already have published results, and the static i18n key for each
// outcome the API reports (#3404).
import { isSectionTestId, effectivePlacesAwarded } from "score-common";

// The report-api federation states (useFederationResults / PublishResults)
// that mean a class's results are already with the federation. DRAFT and no
// row at all mean nothing was published. Reuses the existing status; no new
// tracker.
const PUBLISHED_FEDERATION_STATUSES = new Set(["UPLOADED", "SUBMITTED", "CORRECTED"]);

export function isPublishedFederationStatus(status) {
  return PUBLISHED_FEDERATION_STATUSES.has(status);
}

// A class "has placings" when any of its rides carries one. Placing 0 is
// stored data (#534), so only null/undefined count as unplaced.
export function classHasPlacings(rides) {
  return (rides || []).some((r) => r?.placing != null);
}

// The classes among `targets` whose placings a switch to `nextValue` would
// touch: they already have placings AND the value really changes (a target
// already at `nextValue` is not re-placed by the server either).
export function classesWithPlacingsAtRisk(targets, ridesByClassId, nextValue) {
  return (targets || []).filter(
    (c) => (c.placedByDivision === true) !== (nextValue === true) && classHasPlacings(ridesByClassId?.[c.id])
  );
}

// #3415 -- the same question for Places awarded: the classes among `targets`
// that already have placings AND whose explicit Places awarded would really
// change (unset and null are the same "no explicit value"; only a positive
// integer counts as a value, matching score-common's explicitPlacesAwarded).
export function classesWithPlacingsAtRiskForPlaces(targets, ridesByClassId, nextValue) {
  // An Equitation / Medal class reads unset as 8, so stored 8 and unset are the
  // same setting there; every other class reads unset as no cap.
  const norm = (c, v) => (isSectionTestId(c.testId) ? effectivePlacesAwarded(v) : Number.isInteger(v) && v > 0 ? v : null);
  return (targets || []).filter(
    (c) => norm(c, c.placesAwarded) !== norm(c, nextValue) && classHasPlacings(ridesByClassId?.[c.id])
  );
}

// The API's placingsOutcome -> a STATIC i18n key (keys must never be built
// from data). Anything unrecognised reads as "none": nothing to claim.
export const PLACINGS_OUTCOME_KEYS = Object.freeze({
  replaced: "ring.placedByDivisionOutcomeReplaced",
  cleared: "ring.placedByDivisionOutcomeCleared",
  none: "ring.placedByDivisionOutcomeNone",
});

export function placingsOutcomeKey(outcome) {
  return PLACINGS_OUTCOME_KEYS[outcome] ?? PLACINGS_OUTCOME_KEYS.none;
}
